import React from "react";

import { Link, LoaderFunctionArgs, Outlet, useLoaderData, useMatches, useParams, useRouteError } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { AlertCircleIcon, CalendarIcon, FolderKanbanIcon, LayoutDashboardIcon, LockIcon, RefreshCwIcon, SettingsIcon, TicketIcon, UsersIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { PROJECT_ROLE_STYLES, PROJECT_STATUS_STYLES } from "@/features/projects/config";
import { useProjectNav } from "@/features/projects/hooks/useProjectNav";
import { getMyPermissionsInProject, getProjectById } from "@/features/projects/services/project";
import { Project } from "@/features/projects/types";
import { Ticket } from "@/features/tickets/types/index";
import { EmptyState } from "@/shared/components/EmptyState";
import Error from "@/shared/components/Error";
import { useErrorModal } from "@/shared/hooks/useErrorModal";
import { PERMISSIONS } from "@/shared/lib/constants/permissions";
import { QUERY_EXPIRATIONS, QUERY_KEYS } from "@/shared/lib/constants/queries";
import { cn } from "@/shared/lib/utils/cn";
import { AppError, getRouteErrorMessage } from "@/shared/lib/utils/errors";
import queryClient from "@/shared/lib/utils/queryClient";

const NAV_ITEMS = [
  { label: "Overview", path: "", icon: LayoutDashboardIcon },
  { label: "Tickets", path: "tickets", icon: TicketIcon },
  { label: "Members", path: "members", icon: UsersIcon },
  { label: "Settings", path: "settings", icon: SettingsIcon },
];

export async function projectLayoutLoader({ params }: LoaderFunctionArgs) {
  const permissions = await queryClient.fetchQuery<string[], AppError | Error>({
    queryKey: [QUERY_KEYS.GET_MY_PERMISSIONS_IN_PROJECT, params.id],
    queryFn: () => getMyPermissionsInProject(params.id!),
    staleTime: QUERY_EXPIRATIONS.GET_MY_PERMISSIONS_IN_PROJECT,
    retry: false,
  });

  return { permissions };
}

function ProjectHeaderSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-4">
        <Skeleton className="size-12 shrink-0" />
        <div className="flex flex-col gap-2.5 pt-1">
          <Skeleton className="h-6 w-48" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>
      </div>
      <Skeleton className="h-4 w-full max-w-md" />
      <Skeleton className="h-4 w-2/3 max-w-sm" />
      <div className="flex items-center gap-5">
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="h-3.5 w-28" />
      </div>
    </div>
  );
}

export function ProjectLayoutLoading() {
  return (
    <div className="flex flex-col gap-8">
      <ProjectHeaderSkeleton />
    </div>
  );
}

export function ProjectLayoutError() {
  const error = useRouteError();
  const { title, description, status } = getRouteErrorMessage(error);

  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <div className="flex size-10 items-center justify-center bg-destructive/10">
        <AlertCircleIcon className="size-5 text-destructive" />
      </div>
      <div className="flex flex-col gap-1.5">
        <p className="text-sm font-semibold">{title}</p>
        <p className="max-w-xs text-sm text-muted-foreground">{description}</p>
      </div>
      {status !== undefined && <span className="font-mono text-xs font-semibold text-destructive">HTTP {status}</span>}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
          <RefreshCwIcon />
          Try again
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to="/projects">Back to projects</Link>
        </Button>
      </div>
    </div>
  );
}

export default function ProjectLayout() {
  const { id } = useParams();
  const { permissions } = useLoaderData() as { permissions: string[] };
  const matches = useMatches();

  const canReadProject = permissions.includes(PERMISSIONS.PROJECT_READ);

  const {
    data: project,
    isPending,
    error,
    refetch,
  } = useQuery<Project, AppError | Error>({
    queryKey: [QUERY_KEYS.GET_PROJECT_BY_ID, id],
    queryFn: () => getProjectById(id!),
    enabled: canReadProject,
  });

  const { open: errorModalOpen, setOpen: setErrorModalOpen, handleRetry: handleErrorRetry } = useErrorModal(error, refetch);

  // Pull the ticket key and id from the child route's loader data if we're on a ticket page
  const { ticketKey, ticketId: loaderTicketId } = matches.reduce<{ ticketKey?: string; ticketId?: string }>((found, match) => {
    if (found.ticketKey) return found;
    const data = match.data as { ticket?: Ticket } | undefined;
    if (data?.ticket) {
      return { ticketKey: data.ticket.key, ticketId: data.ticket.id };
    }
    return found;
  }, {});

  const { activeSegment, breadcrumbTrail } = useProjectNav(id!, { ticketKey, ticketId: loaderTicketId });

  const createdAt = project?.createdAt ? new Date(project.createdAt).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : null;

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/projects">Projects</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            {breadcrumbTrail.length === 0 ? (
              <BreadcrumbPage>{project?.name}</BreadcrumbPage>
            ) : (
              <BreadcrumbLink asChild>
                <Link to={`/projects/${id}`}>{project?.name}</Link>
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
          {breadcrumbTrail.map((crumb) => (
            <React.Fragment key={crumb.to}>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {crumb.isLast ? (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.to}>{crumb.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
      {!canReadProject ? (
        <EmptyState
          icon={LockIcon}
          title="No access"
          description="You don't have permission to view this project. Contact an administrator to request access."
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/projects">Back to projects</Link>
            </Button>
          }
        />
      ) : isPending ? (
        <ProjectHeaderSkeleton />
      ) : error ? (
        <Error variant="modal" title="Failed to load project" error={error} open={errorModalOpen} onOpenChange={setErrorModalOpen} onRetry={handleErrorRetry} />
      ) : (
        <>
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center bg-primary/10 text-primary">
                <FolderKanbanIcon className="size-6" />
              </div>
              <div className="flex flex-col gap-2.5">
                <h1 className="font-heading text-2xl font-semibold tracking-normal uppercase leading-none">{project.name}</h1>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className={PROJECT_STATUS_STYLES[project.status]}>
                    {project.status}
                  </Badge>
                  <Badge variant="secondary" className={PROJECT_ROLE_STYLES[project.role.name]}>
                    {project.role.name}
                  </Badge>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{project.description}</p>
            <div className="flex flex-col items-start sm:flex-row sm:items-center gap-5">
              {createdAt && (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CalendarIcon className="size-3.5 shrink-0" />
                  Created {createdAt}
                </span>
              )}
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <TicketIcon className="size-3.5 shrink-0" />
                {project.openTicketCount} open {project.openTicketCount === 1 ? "ticket" : "tickets"}
              </span>
            </div>
          </div>

          <nav className="grid grid-cols-1 gap-1 sm:flex sm:flex-wrap sm:items-center">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const to = item.path ? `/projects/${id}/${item.path}` : `/projects/${id}`;
              const isActive = activeSegment === item.path;
              return (
                <Link
                  key={item.path}
                  to={to}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-semibold tracking-wider uppercase transition-colors sm:justify-start",
                    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  <Icon className="size-3.5 shrink-0" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <Outlet context={{ project, permissions }} />
        </>
      )}
    </div>
  );
}
